import { useEffect, type ReactNode } from "react";
import { useTheme } from "next-themes";
import { useEnterprise } from "@/contexts/EnterpriseContext";

/**
 * Sets the document title and exposes the active enterprise's primary color
 * as a CSS custom property (`--enterprise-primary`) on `<html>`. The selected
 * color follows the active theme — dark mode uses `primaryColorDark` when set,
 * otherwise falls back to `primaryColor`. Must be rendered inside
 * `EnterpriseProvider`.
 */
export function EnterpriseBrandingProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { enterprise } = useEnterprise();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  useEffect(() => {
    if (enterprise?.name) {
      document.title = `${enterprise.name} | Gestionalo`;
    }
  }, [enterprise]);

  useEffect(() => {
    const root = document.documentElement;
    const color = isDark
      ? (enterprise?.primaryColorDark ?? enterprise?.primaryColor ?? null)
      : (enterprise?.primaryColor ?? null);
    if (color) {
      root.style.setProperty("--enterprise-primary", color);
    } else {
      root.style.removeProperty("--enterprise-primary");
    }
    return () => {
      root.style.removeProperty("--enterprise-primary");
    };
  }, [enterprise?.primaryColor, enterprise?.primaryColorDark, isDark]);

  return <>{children}</>;
}

/**
 * Resolves the enterprise primary color for the current theme. Returns
 * `primaryColorDark` in dark mode (falling back to `primaryColor`), or
 * `primaryColor` in light mode. `null` when no branding color is set.
 */
export function useEnterprisePrimaryColor(): string | null {
  const { enterprise } = useEnterprise();
  const { resolvedTheme } = useTheme();
  if (!enterprise) return null;
  if (resolvedTheme === "dark") {
    return enterprise.primaryColorDark ?? enterprise.primaryColor ?? null;
  }
  return enterprise.primaryColor ?? null;
}
